import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { ImageSquare, DownloadSimple, CircleNotch, WhatsappLogo } from "@phosphor-icons/react";

const SIZES = [
  { key: "portrait", label: "Portrait 1080×1350", w: 1080, h: 1350, scale: 0.3 },
  { key: "square", label: "Square 1080×1080", w: 1080, h: 1080, scale: 0.3 },
  { key: "story", label: "Story 1080×1920", w: 1080, h: 1920, scale: 0.21 },
];

// Downloadable "In Loving Memory" poster. QR encodes the memorial page on the
// CURRENT origin, so it always opens the correct domain (alumnipulse.net / indiaalumni.com).
export default function MemorialPoster({ m, mid }) {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(SIZES[0]);
  const [busy, setBusy] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const ref = useRef(null);

  const url = typeof window !== "undefined" ? `${window.location.origin}/memorials/${mid}` : "";
  const hostLabel = typeof window !== "undefined" ? window.location.host : "alumnipulse.net";
  const years = [m.dob, m.dop].filter(Boolean).join(" – ");
  const subtitle = [m.class_batch || m.graduation_year, m.institution].filter(Boolean).join(" · ");
  const quote = m.religious_message || m.epitaph || "";

  useEffect(() => {
    if (!open || !m.photo_file_id) { setPhotoData(null); return; }
    let alive = true;
    fetch(fileUrl(m.photo_file_id)).then((r) => r.blob()).then((b) => new Promise((res) => {
      const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b);
    })).then((d) => { if (alive) setPhotoData(d); }).catch(() => { if (alive) setPhotoData(null); });
    return () => { alive = false; };
  }, [open, m.photo_file_id]);

  const download = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { width: size.w, height: size.h, pixelRatio: 1, cacheBust: true, skipFonts: true, backgroundColor: "#0e1b16" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `memorial-${(m.name || "tribute").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${size.key}.png`;
      a.click();
      toast.success("Poster downloaded");
    } catch (e) { toast.error("Could not render the poster — please try again"); }
    finally { setBusy(false); }
  };

  const shareWhatsApp = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { width: size.w, height: size.h, pixelRatio: 1, cacheBust: true, skipFonts: true, backgroundColor: "#0e1b16" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `memorial-${size.key}.png`, { type: "image/png" });
      const text = `In loving memory of ${m.name}${years ? ` (${years})` : ""} — ${url}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = file.name; a.click();
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
        toast.info("Poster downloaded — attach it in the WhatsApp window");
      }
    } catch (e) { toast.error("Could not prepare the poster for WhatsApp"); }
    finally { setBusy(false); }
  };

  const S = size.w / 1080; // scale text metrics with width
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 rounded-full" data-testid="memorial-poster-btn">
          <ImageSquare size={16} /> Create poster
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Memorial poster</DialogTitle>
          <DialogDescription>A dignified “In Loving Memory” card with a QR to this page. Pick a size and download.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap justify-center gap-2" data-testid="poster-sizes">
          {SIZES.map((s) => (
            <button key={s.key} onClick={() => setSize(s)} data-testid={`poster-size-${s.key}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${size.key === s.key ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-accent"}`}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="mx-auto my-2 overflow-hidden rounded-xl border border-border/60" style={{ width: size.w * size.scale, height: size.h * size.scale }}>
          <div style={{ transform: `scale(${size.scale})`, transformOrigin: "top left", width: size.w, height: size.h }}>
            <div ref={ref} style={{ width: size.w, height: size.h, background: "#0e1b16", color: "#f5f1e8", fontFamily: "Georgia, 'Times New Roman', serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", boxSizing: "border-box", padding: 96 * S, position: "relative" }}>
              <div style={{ position: "absolute", top: 48 * S, right: 48 * S, bottom: 48 * S, left: 48 * S, border: `${2 * S}px solid rgba(212,175,110,0.5)`, borderRadius: 28 * S, pointerEvents: "none" }} />
              <div style={{ letterSpacing: 8 * S, fontSize: 30 * S, color: "#d4af6e", textTransform: "uppercase", marginBottom: 40 * S }}>In Loving Memory</div>
              <div style={{ width: 460 * S, height: 460 * S, borderRadius: "50%", overflow: "hidden", border: `${6 * S}px solid rgba(212,175,110,0.6)`, background: "#1a2b25", display: "grid", placeItems: "center" }}>
                {photoData ? <img src={photoData} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 200 * S, color: "#3a4d45" }}>{(m.name || "?")[0]}</span>}
              </div>
              <div style={{ fontSize: 66 * S, marginTop: 48 * S, textAlign: "center", lineHeight: 1.1, maxWidth: 880 * S }}>{m.name}</div>
              {years && <div style={{ fontSize: 36 * S, marginTop: 16 * S, color: "#c9c2b0" }}>{years}</div>}
              {subtitle && <div style={{ fontSize: 28 * S, marginTop: 18 * S, color: "#a9b3ac", textAlign: "center", maxWidth: 860 * S }}>{subtitle}</div>}
              {m.profession && <div style={{ fontSize: 26 * S, marginTop: 8 * S, color: "#a9b3ac", textAlign: "center", maxWidth: 860 * S }}>{m.profession}</div>}
              {quote && <div style={{ fontSize: 30 * S, marginTop: 34 * S, fontStyle: "italic", color: "#e7ddc7", textAlign: "center", maxWidth: 860 * S, lineHeight: 1.35 }}>“{quote}”</div>}
              <div style={{ marginTop: 48 * S, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 * S }}>
                <div style={{ background: "#fff", padding: 16 * S, borderRadius: 16 * S }}>
                  <QRCodeCanvas value={url} size={Math.round(180 * S)} fgColor="#0e1b16" bgColor="#ffffff" level="M" />
                </div>
                <div style={{ fontSize: 22 * S, color: "#c9c2b0" }}>Scan to visit the memorial</div>
              </div>
              <div style={{ position: "absolute", bottom: 70 * S, fontSize: 22 * S, letterSpacing: 2 * S, color: "#8a938c" }}>{hostLabel}</div>
            </div>
          </div>
        </div>

        <Button onClick={download} disabled={busy} className="w-full rounded-full gap-2" data-testid="poster-download-btn">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
          {busy ? "Rendering…" : `Download ${size.label}`}
        </Button>
        <Button onClick={shareWhatsApp} disabled={busy} variant="outline" className="w-full rounded-full gap-2" style={{ borderColor: "#25D366", color: "#128C4B" }} data-testid="poster-whatsapp-btn">
          <WhatsappLogo size={18} weight="fill" style={{ color: "#25D366" }} /> Send to WhatsApp
        </Button>
      </DialogContent>
    </Dialog>
  );
}
